import React from 'react';
import {
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { Subject, SubjectVersion, Chapter, KnowledgePoint } from '../types';

interface FilterChipsProps {
  // 数据源
  subjects?: Subject[];
  subjectVersions?: SubjectVersion[];
  chapters?: Chapter[];
  knowledgePoints?: KnowledgePoint[];
  
  // 筛选值
  searchTerm?: string;
  selectedSubjectId?: number | '';
  selectedVersionId?: number | '';
  selectedChapterId?: number | '';
  selectedKnowledgePointId?: number | '';
  otherFilters?: Array<{
    key: string;
    label: string;
    value: any;
    displayValue: string;
    onDelete: () => void;
  }>;
  
  // 删除回调
  onRemoveSearch?: () => void;
  onRemoveSubject?: () => void;
  onRemoveVersion?: () => void;
  onRemoveChapter?: () => void;
  onRemoveKnowledgePoint?: () => void;
  
  // 样式
  sx?: any;
}

const FilterChips: React.FC<FilterChipsProps> = ({
  subjects = [],
  subjectVersions = [],
  chapters = [],
  knowledgePoints = [],
  
  searchTerm,
  selectedSubjectId,
  selectedVersionId,
  selectedChapterId,
  selectedKnowledgePointId,
  otherFilters = [],
  
  onRemoveSearch,
  onRemoveSubject,
  onRemoveVersion,
  onRemoveChapter,
  onRemoveKnowledgePoint,
  
  sx = {},
}) => {
  // 计算是否有任何筛选条件
  const hasFilters = !!(
    searchTerm ||
    selectedSubjectId ||
    selectedVersionId ||
    selectedChapterId ||
    selectedKnowledgePointId ||
    otherFilters.length > 0
  );

  if (!hasFilters) return null;

  // 获取显示名称的辅助函数
  const getSubjectName = (id: number | '') => {
    if (!id) return '';
    const subject = subjects.find(s => s.id === id);
    return subject?.name || `科目ID: ${id}`;
  };

  const getVersionDisplayName = (id: number | '') => {
    if (!id) return '';
    
    // 从subjects中找到包含此版本的科目
    for (const subject of subjects) {
      const version = subject.versions?.find(v => v.id === id);
      if (version) {
        return version.schoolLevel 
          ? `${version.name}(${version.schoolLevel})`
          : version.name;
      }
    }
    
    // 如果从subjects中找不到，尝试从subjectVersions中找
    const version = subjectVersions.find(v => v.id === id);
    if (version) {
      return version.schoolLevel 
        ? `${version.name}(${version.schoolLevel})`
        : version.name;
    }
    
    return `版本ID: ${id}`;
  };

  const getChapterName = (id: number | '') => {
    if (!id) return '';
    const chapter = chapters.find(c => c.id === id);
    return chapter?.name || `章节ID: ${id}`;
  };

  const getKnowledgePointName = (id: number | '') => {
    if (!id) return '';
    const kp = knowledgePoints.find(k => k.id === id);
    return kp?.name || `知识点ID: ${id}`;
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', ...sx }}>
      <Typography variant="body2" color="text.secondary">
        当前筛选:
      </Typography>
      
      {/* 搜索条件 */}
      {searchTerm && (
        <Chip
          label={`搜索: ${searchTerm}`}
          size="small"
          onDelete={onRemoveSearch}
          color="primary"
        />
      )}
      
      {/* 科目筛选 */}
      {selectedSubjectId && (
        <Chip
          label={`科目: ${getSubjectName(selectedSubjectId)}`}
          size="small"
          onDelete={onRemoveSubject}
          color="primary"
        />
      )}
      
      {/* 教材版本筛选 */}
      {selectedVersionId && (
        <Chip
          label={`教材版本: ${getVersionDisplayName(selectedVersionId)}`}
          size="small"
          onDelete={onRemoveVersion}
          color="primary"
        />
      )}
      
      {/* 章节筛选 */}
      {selectedChapterId && (
        <Chip
          label={`章节: ${getChapterName(selectedChapterId)}`}
          size="small"
          onDelete={onRemoveChapter}
          color="primary"
        />
      )}
      
      {/* 知识点筛选 */}
      {selectedKnowledgePointId && (
        <Chip
          label={`知识点: ${getKnowledgePointName(selectedKnowledgePointId)}`}
          size="small"
          onDelete={onRemoveKnowledgePoint}
          color="primary"
        />
      )}
      
      {/* 其他筛选条件 */}
      {otherFilters.map((filter) => (
        <Chip
          key={filter.key}
          label={`${filter.label}: ${filter.displayValue}`}
          size="small"
          onDelete={filter.onDelete}
          color="primary"
        />
      ))}
    </Box>
  );
};

export default FilterChips;